import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Pencil, Trash2, Plus } from "lucide-react";
import { fmtDuration, PRIORITY_META, metaLabel } from "@/lib/supportUi";

const PRIOS = ["low", "normal", "high", "urgent", "critical"];
const MACRO_LANGS = ["fr", "en", "sw"];

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-bold text-white">{value}</div>
    </div>
  );
}

export function SupportSlaPanel({ base, tr }) {
  const [metrics, setMetrics] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [csat, setCsat] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`${base}/metrics`).then((r) => setMetrics(r.data)).catch(() => setMetrics({}));
    api.get(`${base}/sla`).then((r) => setCfg(r.data)).catch(() => {});
    api.get(`${base}/csat`).then((r) => setCsat(r.data)).catch(() => {});
  }, [base]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`${base}/sla`, {
        sla_targets: cfg.sla_targets,
        autoclose_hours: cfg.autoclose_hours,
        autoclose_enabled: cfg.autoclose_enabled,
      });
      setCfg(data);
      toast.success(tr("adminsup.sla_saved", "Paramètres SLA enregistrés"));
    } catch (e) { toast.error(e.response?.data?.detail || tr("td.error", "Erreur")); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-4 space-y-4 rounded-xl border border-slate-800 bg-slate-900 p-4" data-testid="admin-sla-panel">
      <div>
        <h3 className="mb-2 text-sm font-semibold text-white">{tr("adminsup.metrics_title", "Métriques de support")}</h3>
        {!metrics ? <Loader2 className="h-5 w-5 animate-spin text-accent" /> : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label={tr("adminsup.m.frt_avg", "1re réponse (moy.)")} value={fmtDuration(metrics.first_response_avg)} />
            <Stat label={tr("adminsup.m.frt_med", "1re réponse (méd.)")} value={fmtDuration(metrics.first_response_median)} />
            <Stat label={tr("adminsup.m.res_avg", "Résolution (moy.)")} value={fmtDuration(metrics.resolution_avg)} />
            <Stat label={tr("adminsup.m.res_med", "Résolution (méd.)")} value={fmtDuration(metrics.resolution_median)} />
            <Stat label={tr("adminsup.m.met", "SLA respectés")} value={metrics.sla_met ?? 0} />
            <Stat label={tr("adminsup.m.breached", "SLA dépassés")} value={metrics.sla_breached ?? 0} />
          </div>
        )}
      </div>

      {csat && (
        <div data-testid="admin-csat">
          <h3 className="mb-2 text-sm font-semibold text-white">{tr("adminsup.csat_title", "Satisfaction client (CSAT)")}</h3>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label={tr("adminsup.csat_avg", "Note moyenne")} value={csat.average != null ? `${csat.average} / 5` : "—"} />
            <Stat label={tr("adminsup.csat_count", "Réponses")} value={csat.count ?? 0} />
            <Stat label={tr("adminsup.csat_low", "Notes ≤ 2")} value={(csat.low_ratings || []).length} />
            <Stat label={tr("adminsup.csat_dist", "5★ / 1★")} value={`${csat.distribution?.[5] ?? 0} / ${csat.distribution?.[1] ?? 0}`} />
          </div>
          {(csat.low_ratings || []).length > 0 && (
            <div className="mt-2 space-y-1">
              {csat.low_ratings.slice(0, 5).map((r, i) => (
                <div key={i} className="rounded-lg border border-red-800/40 bg-red-500/5 px-3 py-1.5 text-xs text-slate-300" data-testid={`csat-low-${i}`}>
                  <span className="font-semibold text-red-300">{r.rating}★</span> · {r.subject || "—"} <span className="text-slate-500">({r.user_email})</span>{r.comment ? ` — ${r.comment}` : ""}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {cfg && (
        <div>
          <h3 className="mb-2 text-sm font-semibold text-white">{tr("adminsup.sla_targets", "Objectifs SLA par priorité (minutes)")}</h3>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {PRIOS.map((p) => (
              <div key={p}>
                <label className="text-[10px] uppercase text-slate-500">{metaLabel(PRIORITY_META[p], tr)}</label>
                <input type="number" min={1} value={cfg.sla_targets?.[p] ?? ""} data-testid={`sla-target-${p}`}
                  onChange={(e) => setCfg({ ...cfg, sla_targets: { ...cfg.sla_targets, [p]: Number(e.target.value) } })}
                  className="w-full rounded-lg border border-slate-700 bg-slate-800 px-2 py-1.5 text-sm text-white outline-none" />
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-4">
            <div>
              <label className="text-[10px] uppercase text-slate-500">{tr("adminsup.autoclose_h", "Fermeture auto après (heures)")}</label>
              <input type="number" min={1} value={cfg.autoclose_hours ?? ""} data-testid="sla-autoclose-hours"
                onChange={(e) => setCfg({ ...cfg, autoclose_hours: Number(e.target.value) })}
                className="w-40 rounded-lg border border-slate-700 bg-slate-800 px-2 py-1.5 text-sm text-white outline-none" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={!!cfg.autoclose_enabled} data-testid="sla-autoclose-enabled"
                onChange={(e) => setCfg({ ...cfg, autoclose_enabled: e.target.checked })} />
              {tr("adminsup.autoclose_on", "Fermeture automatique activée")}
            </label>
            <button onClick={save} disabled={busy} data-testid="sla-save" className="ml-auto inline-flex items-center gap-2 rounded-full bg-accent px-5 py-2 text-sm font-medium text-white disabled:opacity-50">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {tr("adminsup.save", "Enregistrer")}
            </button>
          </div>
        </div>
      )}
      {cfg && <MacrosManager base={base} tr={tr} />}
    </div>
  );
}

function MacrosManager({ base, tr }) {
  const [list, setList] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`${base}/macros?include_inactive=true`).then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, [base]);
  const blank = { title: "", content: "", language: "fr", category: "general", active: true };
  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) return toast.error(tr("adminsup.macro_req", "Titre et contenu requis"));
    setBusy(true);
    try {
      if (form.id) await api.put(`${base}/macros/${form.id}`, form);
      else await api.post(`${base}/macros`, form);
      toast.success(tr("adminsup.macro_saved", "Macro enregistrée")); setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`${base}/macros/${id}`); load(); } catch { toast.error(tr("common.error", "Erreur")); } };
  return (
    <div data-testid="admin-macros">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">{tr("adminsup.macros_title", "Réponses préenregistrées (macros)")}</h3>
        <button onClick={() => setForm(blank)} data-testid="macro-new" className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1.5 text-xs font-medium text-white"><Plus className="h-3.5 w-3.5" /> {tr("adminsup.macro_add", "Nouvelle macro")}</button>
      </div>
      {form && (
        <div className="mb-3 space-y-2 rounded-lg border border-slate-700 bg-slate-950/40 p-3" data-testid="macro-form">
          <div className="flex gap-2">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={tr("adminsup.macro_title_ph", "Titre")} data-testid="macro-title" className="flex-1 rounded-lg border border-slate-700 bg-slate-800 px-2 py-1.5 text-sm text-white outline-none" />
            <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} data-testid="macro-language" className="rounded-lg border border-slate-700 bg-slate-800 px-2 py-1.5 text-sm text-white">
              {MACRO_LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
            </select>
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder={tr("adminsup.macro_cat", "Catégorie")} data-testid="macro-category" className="w-32 rounded-lg border border-slate-700 bg-slate-800 px-2 py-1.5 text-sm text-white outline-none" />
          </div>
          <textarea rows={3} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder={tr("adminsup.macro_content_ph", "Contenu — variables: {{client_name}} {{order_id}} {{service_name}} {{order_status}}")} data-testid="macro-content" className="w-full rounded-lg border border-slate-700 bg-slate-800 px-2 py-1.5 text-sm text-white outline-none" />
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-slate-300"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="macro-active" /> {tr("adminsup.macro_activef", "Active")}</label>
            <button onClick={save} disabled={busy} data-testid="macro-save" className="ml-auto rounded-full bg-accent px-4 py-1.5 text-xs font-medium text-white disabled:opacity-50">{tr("adminsup.save", "Enregistrer")}</button>
            <button onClick={() => setForm(null)} className="rounded-full border border-slate-700 px-4 py-1.5 text-xs text-slate-300">{tr("td.cancel", "Annuler")}</button>
          </div>
        </div>
      )}
      {list === null ? <Loader2 className="h-5 w-5 animate-spin text-accent" /> : (
        <div className="divide-y divide-slate-800 rounded-lg border border-slate-800">
          {list.map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`macro-row-${m.id}`}>
              <div className="min-w-0"><div className="truncate text-sm text-white">{m.title} <span className="text-[10px] text-slate-500">· {m.language.toUpperCase()} · {m.category}{m.active ? "" : " · inactive"}</span></div><div className="truncate text-xs text-slate-500">{m.content}</div></div>
              <div className="flex shrink-0 gap-1">
                <button onClick={() => setForm(m)} data-testid={`macro-edit-${m.id}`} className="rounded p-1.5 text-slate-400 hover:bg-slate-800"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(m.id)} data-testid={`macro-del-${m.id}`} className="rounded p-1.5 text-red-400 hover:bg-slate-800"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          {!list.length && <div className="px-3 py-6 text-center text-xs text-slate-500">{tr("adminsup.no_macro", "Aucune macro. Créez-en une pour accélérer les réponses.")}</div>}
        </div>
      )}
    </div>
  );
}
